import { supabase, deleteStorageFileByUrl, deleteUserStorageFolder, deleteFolderContents } from './authService';
import { User, NewsItem, CommentWithUser, Language, Notice } from '../types';

const fetchAllRecords = async (table: string, select: string = '*') => {
    let allData: any[] = [];
    let from = 0;
    const step = 1000;
    while (true) {
        const { data, error } = await supabase.from(table).select(select).range(from, from + step - 1);
        if (error || !data || data.length === 0) break;
        allData = [...allData, ...data];
        if (data.length < step) break;
        from += step;
    }
    return allData;
};

export const fetchAllUsers = async (): Promise<User[]> => {
    const data = await fetchAllRecords('user', '*');
    return (data || []).map(u => ({
        id: u.id, name: u.name, email: u.email, avatar: u.avatar_url,
        backgroundUrl: u.background_url, isPro: u.is_pro || false,
        joinDate: u.created_at, lang: u.lang, role: u.role
    })).sort((a, b) => new Date(b.joinDate).getTime() - new Date(a.joinDate).getTime());
};

/**
 * 뉴스 기사를 영구 삭제합니다 (Storage 파일 및 모든 연관 테이블 포함)
 */
export const deleteNewsPermanently = async (newsId: string): Promise<void> => {
    try {
        const id = newsId.toString();
        // 1. 뉴스 데이터 가져오기 (이미지 URL 확인용)
        const { data: newsItem } = await supabase.from('news_list').select('image, type').eq('id', id).maybeSingle();

        // 2. 뉴스 댓글 목록 가져오기
        const { data: comments } = await supabase.from('news_comments').select('id').eq('news_id', id);
        const commentIds = (comments || []).map(c => c.id);

        // 3. 연관 데이터 삭제 (에러가 발생해도 중단되지 않도록 개별 처리)
        const deleteRelated = async (table: string, column: string, value: any) => {
            try {
                await supabase.from(table).delete().eq(column, value);
            } catch (e) {
                console.warn(`Failed to delete from ${table}:`, e);
            }
        };

        if (commentIds.length > 0) {
            try {
                await supabase.from('news_comments_reports').delete().in('news_comments_id', commentIds);
                await supabase.from('news_comments_likes').delete().in('news_comments_id', commentIds);
            } catch (e) {
                console.warn("Failed to delete comment interactions:", e);
            }
        }

        await Promise.all([
            deleteRelated('news_comments', 'news_id', id),
            deleteRelated('news_views', 'news_id', id),
            deleteRelated('news_reports', 'news_id', id),
            deleteRelated('point_logs', 'news_id', id)
        ]);

        // 4. 뉴스 원본 레코드 삭제
        const { error: deleteError } = await supabase.from('news_list').delete().eq('id', id);
        if (deleteError) throw deleteError;

        // 5. 스토리지 파일 및 가상 폴더 삭제
        try {
            // 메인 이미지 삭제
            if (newsItem?.image) {
                await deleteStorageFileByUrl(newsItem.image);
            }
            
            // TTS 폴더 삭제 (재귀)
            await deleteFolderContents(`news-tts/${newsId}`);

            // 매거진 타입인 경우 special 폴더 내 관련 파일 삭제
            if (newsItem?.type === 'magazine') { 
                await deleteFolderContents(`special/${newsId}`);
            }
        } catch (storageError) {
            console.warn("Storage removal partially failed, but DB records cleared:", storageError);
        }
        
    } catch (e) {
        console.error("Critical error during news deletion:", e);
        throw e;
    }
};

/**
 * 사용자를 영구 삭제합니다
 */
export const deleteUserPermanently = async (userId: string): Promise<void> => {
    try {
        const { data: userNews } = await supabase.from('news_list').select('id').eq('user_id', userId);
        if (userNews && userNews.length > 0) {
            for (const news of userNews) {
                await deleteNewsPermanently(news.id);
            }
        }

        await Promise.all([
            supabase.from('comments').delete().eq('user_id', userId),
            supabase.from('comment_likes').delete().eq('user_id', userId),
            supabase.from('comment_reports').delete().eq('reporter_id', userId),
            supabase.from('news_comments_reports').delete().eq('reporter_id', userId),
            supabase.from('league_selections').delete().eq('user_id', userId),
            supabase.from('news_views').delete().eq('user_id', userId),
            supabase.from('news_comments').delete().eq('user_id', userId)
        ]);

        const { error: userDeleteError } = await supabase.from('user').delete().eq('id', userId);
        if (userDeleteError) throw userDeleteError;

        await deleteUserStorageFolder(userId);

    } catch (e) {
        console.error("User deletion process failed:", e);
        throw e;
    }
};

export const fetchUserFullActivity = async (userId: string) => {
    const [newsRes, newsCommentsRes, matchCommentsRes, leaguesRes] = await Promise.all([
        supabase.from('news_list').select('*').eq('user_id', userId).order('published_at', { ascending: false }),
        supabase.from('news_comments').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('comments').select('*').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('league_selections').select('league_id').eq('user_id', userId)
    ]);
    const combinedComments = [
        ...(newsCommentsRes.data || []).map(c => ({ ...c, type: 'News' })),
        ...(matchCommentsRes.data || []).map(c => ({ ...c, type: 'Match' }))
    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return { news: newsRes.data || [], comments: combinedComments, leagues: (leaguesRes.data || []).map(l => l.league_id) };
};

export const fetchAllNews = async (): Promise<NewsItem[]> => {
    const data = await fetchAllRecords('news_list', '*');
    return (data || []).map(item => ({
        id: item.id.toString(), 
        title: item.title_ko || item.title_en || 'No Title',
        summary: item.summary_ko || item.summary_en || '',
        publishedAt: item.published_at, source: item.source, image: item.image,
        user_id: item.user_id, type: item.type, 
        teams: item.teams, 
        players: item.players,
        is_reported: item.is_reported || false,
        is_hidden: item.is_hidden || false,
        created_at: item.created_at,
        time: item.published_at ? new Date(item.published_at).toLocaleDateString() : 'Unknown'
    })).sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
};

export const toggleNewsVisibility = async (newsId: string, isHidden: boolean): Promise<void> => {
    const { error } = await supabase.from('news_list').update({ is_hidden: isHidden }).eq('id', newsId);
    if (error) throw error;
};

export const fetchNewsReports = async (newsId: string) => {
    const { data } = await supabase.from('news_reports').select('*, reporter:user!reporter_id(name)').eq('news_id', newsId);
    return data || [];
};

export const fetchAllReports = async (startDate?: string, endDate?: string) => {
    let matchCommentQuery = supabase.from('comment_reports').select('*, target:comments!comment_id(content, post_id), reporter:user!reporter_id(name)');
    let newsCommentQuery = supabase.from('news_comments_reports').select('*, target:news_comments!news_comments_id(content, news_id), reporter:user!reporter_id(name)');
    let newsArticleQuery = supabase.from('news_reports').select('*, target:news_list!news_id(title_ko, title_en), reporter:user!reporter_id(name)');

    if (startDate) {
        const start = `${startDate}T00:00:00Z`;
        matchCommentQuery = matchCommentQuery.gte('created_at', start);
        newsCommentQuery = newsCommentQuery.gte('created_at', start);
        newsArticleQuery = newsArticleQuery.gte('created_at', start);
    }
    if (endDate) {
        const end = `${endDate}T23:59:59Z`;
        matchCommentQuery = matchCommentQuery.lte('created_at', end);
        newsCommentQuery = newsCommentQuery.lte('created_at', end);
        newsArticleQuery = newsArticleQuery.gte('created_at', end);
    }

    const [matchRes, newsRes, articleRes] = await Promise.all([
        matchCommentQuery.order('created_at', { ascending: false }),
        newsCommentQuery.order('created_at', { ascending: false }),
        newsArticleQuery.order('created_at', { ascending: false })
    ]);

    const combined = [
        ...(matchRes.data || []).map(r => ({ 
            id: `mr-${r.id}`, type: 'match_comment', target_id: (r as any).target?.post_id, item_id: r.comment_id,
            target_content: (r as any).target?.content || '이미 삭제된 댓글입니다', 
            reason: r.reason, reporter_name: r.reporter?.name || '익명', created_at: r.created_at 
        })),
        ...(newsRes.data || []).map(r => ({ 
            id: `ncr-${r.id}`, type: 'news_comment', target_id: (r as any).target?.news_id, item_id: r.news_comments_id,
            target_content: (r as any).target?.content || '이미 삭제된 댓글입니다', 
            reason: r.reason, reporter_name: r.reporter?.name || '익명', created_at: r.created_at 
        })),
        ...(articleRes.data || []).map(r => ({ 
            id: `nr-${r.id}`, type: 'news', target_id: r.news_id, item_id: r.news_id,
            target_content: (r as any).target?.title_ko || (r as any).target?.title_en || '이미 삭제된 기사입니다', 
            reason: r.reason, reporter_name: r.reporter?.name || '익명', created_at: r.created_at 
        }))
    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return combined;
};

export const updateNewsItem = async (newsId: string, updates: any): Promise<void> => {
    const { error } = await supabase.from('news_list').update(updates).eq('id', newsId);
    if (error) throw error;
};

export const updateUserRole = async (userId: string, role: 'user' | 'admin'): Promise<void> => {
    const { error } = await supabase.from('user').update({ role }).eq('id', userId);
    if (error) throw error;
};

export const fetchAdminStats = async () => {
    const [usersRes, newsRes, commentsRes, newsCommentsRes, matchReports, newsReports, newsCommentReports] = await Promise.all([
        fetchAllRecords('user', 'created_at'),
        fetchAllRecords('news_list', 'published_at'),
        fetchAllRecords('comments', 'created_at'),
        fetchAllRecords('news_comments', 'created_at'),
        supabase.from('comment_reports').select('id', { count: 'exact', head: true }),
        supabase.from('news_reports').select('id', { count: 'exact', head: true }),
        supabase.from('news_comments_reports').select('id', { count: 'exact', head: true })
    ]);
    
    const now = new Date();
    const lastMonth = new Date(now); lastMonth.setMonth(now.getMonth() - 1);
    const newUsersLastMonth = (usersRes || []).filter(u => new Date(u.created_at) >= lastMonth).length;
    const today = new Date(); today.setHours(0,0,0,0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const postsToday = (newsRes || []).filter(n => new Date(n.published_at) >= today).length;
    const postsYesterday = (newsRes || []).filter(n => { const d = new Date(n.published_at); return d >= yesterday && d < today; }).length;
    
    let activityChange = '0';
    if (postsYesterday > 0) activityChange = (((postsToday - postsYesterday) / postsYesterday) * 100).toFixed(1);
    else if (postsToday > 0) activityChange = '100';

    const totalReports = (matchReports.count || 0) + (newsReports.count || 0) + (newsCommentReports.count || 0);

    return { 
        users: usersRes.length, 
        usersGrowth: newUsersLastMonth, 
        posts: newsRes.length, 
        activityChange, 
        comments: (commentsRes?.length || 0) + (newsCommentsRes?.length || 0), 
        reports: totalReports 
    };
};

export const fetchAdminChartsData = async (range: '7d' | '30d' | '90d' = '30d') => {
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const startDate = new Date(); startDate.setDate(startDate.getDate() - days); startDate.setHours(0,0,0,0);
    const [usersRes, newsRes, selectionsRes, highlightsRes, shortsRes] = await Promise.all([
        supabase.from('user').select('created_at').gte('created_at', startDate.toISOString()),
        supabase.from('news_list').select('published_at, type').gte('published_at', startDate.toISOString()),
        supabase.from('league_selections').select('league_id'),
        supabase.from('match_highlights').select('match_date').gte('match_date', startDate.toISOString()),
        supabase.from('youtube_videos').select('created_at').gte('created_at', startDate.toISOString())
    ]);
    const dailyMap = new Map<string, { registrations: number, activity: number, highlights: number, shorts: number }>();
    for (let i = 0; i <= days; i++) {
        const d = new Date(startDate); d.setDate(d.getDate() + i);
        const dateStr = d.toISOString().split('T')[0];
        dailyMap.set(dateStr, { registrations: 0, activity: 0, highlights: 0, shorts: 0 });
    }
    (usersRes.data || []).forEach(u => { const dateStr = new Date(u.created_at).toISOString().split('T')[0]; if (dailyMap.has(dateStr)) dailyMap.get(dateStr)!.registrations++; });
    (newsRes.data || []).forEach(n => { const dateStr = new Date(n.published_at).toISOString().split('T')[0]; if (dailyMap.has(dateStr)) dailyMap.get(dateStr)!.activity++; });
    (highlightsRes.data || []).forEach(h => { const dateStr = new Date(h.match_date).toISOString().split('T')[0]; if (dailyMap.has(dateStr)) dailyMap.get(dateStr)!.highlights++; });
    (shortsRes.data || []).forEach(s => { const dateStr = new Date(s.created_at).toISOString().split('T')[0]; if (dailyMap.has(dateStr)) dailyMap.get(dateStr)!.shorts++; });
    const dailyStats = Array.from(dailyMap.entries()).map(([date, counts]) => ({ date: date.substring(5).replace('-', '/'), ...counts }));
    const sectorMap = new Map<string, number>();
    (selectionsRes.data || []).forEach(s => { const name = String(s.league_id); sectorMap.set(name, (sectorMap.get(name) || 0) + 1); });
    const leagueData = Array.from(sectorMap.entries()).map(([name, count]) => ({ name: name.toUpperCase(), count })).sort((a, b) => b.count - a.count);
    return { leagueData, dailyStats };
};

export const fetchRecentAdminAlerts = async () => {
    const threeDaysAgo = new Date(); threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    const [usersRes, matchReportsRes, newsReportsRes, newsCommentReportsRes, newsPostsRes] = await Promise.all([
        supabase.from('user').select('id, name, email, created_at').gte('created_at', threeDaysAgo.toISOString()),
        supabase.from('comment_reports').select('id, reporter_id, reason, created_at').gte('created_at', threeDaysAgo.toISOString()),
        supabase.from('news_reports').select('id, reporter_id, reason, created_at').gte('created_at', threeDaysAgo.toISOString()),
        supabase.from('news_comments_reports').select('id, reporter_id, reason, created_at').gte('created_at', threeDaysAgo.toISOString()),
        supabase.from('news_list').select('id, title_ko, title_en, source, published_at').gte('published_at', threeDaysAgo.toISOString())
    ]);
    const userAlerts = (usersRes.data || []).map(u => ({ id: `u-${u.id}`, type: 'user', title: u.name, time: u.created_at, data: u }));
    const matchReportAlerts = (matchReportsRes.data || []).map(r => ({ id: `mr-${r.id}`, type: 'report', title: `Match Talk: ${r.reason}`, time: r.created_at, data: r }));
    const newsReportAlerts = (newsReportsRes.data || []).map(r => ({ id: `nr-${r.id}`, type: 'report', title: `Intel Breach: ${r.reason}`, time: r.created_at, data: r }));
    const newsCommentReportAlerts = (newsCommentReportsRes.data || []).map(r => ({ id: `ncr-${r.id}`, type: 'report', title: `Feed Comment: ${r.reason}`, time: r.created_at, data: r }));
    const newsPostAlerts = (newsPostsRes.data || []).map(n => ({ id: `np-${n.id}`, type: 'user', title: `New Intel: ${n.title_ko || n.title_en}`, time: n.published_at, data: { email: `Source: ${n.source}` } }));
    
    // 1:1 Inquiries
    const { data: inquiriesRes } = await supabase.from('inquiries').select('id, title, created_at, status').gte('created_at', threeDaysAgo.toISOString());
    const inquiryAlerts = (inquiriesRes || []).map(i => ({
        id: `inq-${i.id}`,
        type: 'inquiry',
        title: `1:1 Inquiry: ${i.title}`,
        time: i.created_at,
        data: { status: i.status }
    }));

    return [...userAlerts, ...matchReportAlerts, ...newsReportAlerts, ...newsCommentReportAlerts, ...newsPostAlerts, ...inquiryAlerts].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
};

export const fetchAllNotices = async (): Promise<Notice[]> => {
  const { data, error } = await supabase.from('notices').select('*').order('created_at', { ascending: false });
  if (error) {
    console.error('Error fetching notices:', error);
    throw error;
  }
  return (data || []).map(item => ({
    ...item,
    id: item.id.toString(),
    user_id: item.user_id 
  }));
};

export const upsertNotice = async (notice: Partial<Notice>): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Unauthorized');
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, created_at, updated_at, ...updateData } = notice as any;
  
  const payload = { 
    ...updateData, 
    // created_by: notice.created_by || user.id, 
    user_id: notice.user_id || user.id,
    updated_at: new Date().toISOString() 
  };
  
  if (id) {
    const { error } = await supabase.from('notices').update(payload).eq('id', id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('notices').insert(payload);
    if (error) throw error;
  }
};

export const deleteNotice = async (id: string): Promise<void> => {
  const { error } = await supabase.from('notices').delete().eq('id', id);
  if (error) throw error;
};

export const fetchAllMatchPredictions = async () => {
  const data = await fetchAllRecords('match_predictions', '*');
  return (data || []).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

export const deleteMatchPrediction = async (id: string): Promise<void> => {
  const { error } = await supabase.from('match_predictions').delete().eq('id', id);
  if (error) throw error;
};

export const fetchActiveNotice = async (): Promise<Notice | null> => {
  const now = new Date().toISOString();
  const { data, error } = await supabase.from('notices').select('*').eq('is_published', true).or(`start_at.is.null,start_at.lte.${now}`).or(`end_at.is.null,end_at.gte.${now}`).order('created_at', { ascending: false }).limit(1).maybeSingle();
  return error ? null : data;
};
